<template>
  <div class="w-full max-w-[520px] flex items-center justify-center">
    <div class="" v-if="step === 1">
      <h1
        class="font-interDisplay text-4xl md:text-[48px] lg:text-[64px] leading-[1] spacing-[-.03em] font-semibold text-dark text-left"
      >
        {{ $t("register_text") }}
      </h1>
      <p
        class="font-interDisplay mt-2 md:mt-5 font-semibold text-sm md:text-lg leading-130 text-gray-380"
      >
        {{ $t("fill_your_info") }}
      </p>

      <div class="flex flex-col gap-4 my-8">
        <FInput
          :placeholder="$t('reg_enter_name')"
          v-model="form.values.full_name"
          :error="form.$v.value.name?.$error"
          maxlength="40"
          prefix-class="text-xl text-gray-450 font-semibold flex items-center mr-3 leading-130"
        >
          <template #prefix>
            <i class="icon-personal-info" />
          </template>
        </FInput>
        <FInputPhone
          v-model="form.values.phone_number"
          placeholder="+99800 000 00 00"
          :error="form.$v.value.phone_number?.$error"
          maxlength="17"
        />
        <FInput
          placeholder="example@hbs.uz"
          v-model.trim="form.values.email"
          :error="form.$v.value.email?.$error"
          prefix-class="text-2xl text-gray-450 font-semibold flex items-center mr-3 leading-130"
        >
          <template #prefix>
            <i class="icon-email" />
          </template>
        </FInput>
        <CountrySelect
          :countries="regions"
          v-model:country="form.values.country"
          v-model:search="search"
          @handleLoadMoreCountry="onLoadCountries"
        />
        <FCheckbox
          :checked="form.values.showReferral"
          :label="$t('referral_number')"
          v-model="form.values.showReferral"
        />
        <FInput
          v-if="form.values.showReferral"
          :placeholder="$t('enter_referral_number')"
          v-model.trim="form.values.referral_number"
          :error="form.$v.value.name?.$error"
          maxlength="40"
        />
      </div>

      <CButton
        class="w-full"
        :text="$t('register')"
        :loading="emailCheckLoading"
        @click="onSubmit"
        :disabled="form.$v.value.$invalid || emailCheckLoading"
      />
    </div>
    <CForgetRecovery
      class="w-full mx-auto max-w-[520px]"
      v-if="step === 2"
      type="create"
      :reg-loading="registerLoading"
      :log-loading="loginLoading"
      @back="step = 1"
      @submit-password="onRegisterConfirm"
    />
    <CConfirmDialog
      :show="termsShow"
      :data="single"
      :reg-loading="registerLoading"
      :log-loading="loginLoading"
      @submit="onRegisterAndLoginForm"
      @close="termsShow = false"
    />
  </div>
</template>

<script setup lang="ts">
import FInput from "@/components/Form/Input/FInput.vue";
import FSelect from "@/components/Form/Select/FSelect.vue";
import CButton from "@/components/Common/CButton.vue";
import ApiService from "@/services/ApiService";
import { onMounted, ref, watch } from "vue";
import { useHandleError } from "@/composables/useHandleError";
import FInputPhone from "@/components/Form/Input/FInputPhone.vue";
import { useCustomToast } from "@/composables/useCustomToast";
import FCheckbox from "@/components/Form/FCheckbox.vue";
import { debounce } from "@/utils";
import { useAuthStore } from "@/modules/Auth/stores";
import { useI18n } from "vue-i18n";
import { useForm } from "@/composables/useForm";
import { email, maxLength, required } from "@vuelidate/validators";
import { useRoute } from "vue-router";
import { JwtService } from "@/services/JwtService";
import apiService from "@/services/ApiService";
import { trackCustomEvent } from "@/utils/fbq";

import CConfirmDialog from "@/components/Common/Dialog/CConfirmDialog.vue";
import CForgetRecovery from "@/modules/Auth/components/CForgetRecovery.vue";
import CountrySelect from "@/modules/Auth/components/CountrySelect.vue";

import { usePushToDataLayer } from "@/composables/usePushToDataLayer";
import { useLocalizedNavigation } from "@/composables/useLocalizedNavigation";
const { pushToDataLayer } = usePushToDataLayer();
const { t } = useI18n();
const route = useRoute();
const { handleError } = useHandleError();
const { showToast } = useCustomToast();
const authStore = useAuthStore();
const { navigateTo } = useLocalizedNavigation();

const form = useForm(
  {
    full_name: "",
    country: "",
    phone_number: "",
    referral_number: route.query.referral_number || "",
    email: "",
    password: "",
    showReferral: false,
  },
  {
    full_name: {
      required,
      maxLength: maxLength(40),
    },
    country: {
      required,
    },
    phone_number: {
      required,
    },
    email: {
      required,
      email,
    },
  },
);

const isFocused = ref(false);

const loading = ref(false);
const loginLoading = ref(false);

const emailCheckLoading = ref(false);
const regions = ref([]);
const countriesCount = ref(2);
const currentPage = ref(1);
const termsShow = ref(false);
const registerLoading = ref(false);

const step = ref(1);

const search = ref("");

function onRegisterForm(password: string): Promise<boolean> {
  return new Promise((resolve, reject) => {
    form.values.password = password;
    const data = {
      full_name: form.values.full_name,
      phone_number: form.values.phone_number?.replace(/\s/g, ""),
      country: form.values.country,
      email: form.values.email?.toLowerCase(),
      password: password,
      referral_number: form.values.referral_number,
    };
    registerLoading.value = true;
    pushToDataLayer("register_attempt", {
      event_category: "form",
      event_label: "register_validation",
      button_text: "Register",
    });
    trackCustomEvent("UserRegisterAttempt", {
      event_category: "email",
      event_label: "register_attempt",
      button_text: "Register",
    });
    ApiService.post("/user/auth/register/", data)
      .then(() => {
        pushToDataLayer("register_success", {
          event_category: "form",
          event_label: "register_success",
          button_text: "Register",
        });
        trackCustomEvent("UserRegisterSuccess", {
          event_category: "form",
          event_label: "register_success",
          button_text: "Register",
        });
        showToast(t("verified_title"), "success");
        resolve(true);
      })
      .catch((err) => {
        reject(err);
        if (err) {
          if (err?.response.status === 429) {
            navigateTo({ name: "PAuthBlocked" });
            showToast(`${err?.response.data.detail}`, "error");
          }
          if (err?.response.status === 400) {
            if (err?.response?.data?.email) {
              showToast(`${err?.response?.data?.email?.[0]}`, "error");
            }
            if (err?.response?.data?.password) {
              showToast(`${err?.response?.data?.password?.[0]}`, "error");
            }
          }
        }
        pushToDataLayer("register_error", {
          event_category: "form",
          event_label: "register_error",
          button_text: "Register",
        });
      })
      .finally(() => (registerLoading.value = false));
  });
}

const passwordValue = ref("");
const onRegisterConfirm = (password: string) => {
  passwordValue.value = password;
  termsShow.value = true;
};

const onRegisterAndLoginForm = async () => {
  await onRegisterForm(passwordValue.value).then(async (res) => {
    if (res) {
      loginLoading.value = true;
      const data = {
        email: form.values.email?.toLowerCase(),
        password: form.values.password,
      };
      await authStore
        .login(data)
        .then(async (res: any) => {
          JwtService.saveToken(res?.data?.tokens?.access);
          JwtService.saveRefreshToken(res?.data?.tokens?.refresh);
          apiService.setHeader();
          await authStore.fetchUserData();
          await navigateTo({ name: "PDashboard" });
          showToast(t("successfully_entered"), "success");
        })
        .catch((err) => {
          if (err?.response?.status === 429) {
            navigateTo({ name: "PAuthBlocked" });
            authStore.blockedTime = +err?.response?.headers["retry-after"];
          } else if (err?.response?.status === 500) {
            showToast(t("some_error"), "error");
          } else {
            showToast(
              err?.response?.data?.[0]?.error?.message ||
                t("check_login_password"),
              "error",
            );
          }
        })
        .finally(() => {
          loginLoading.value = false;
        });
    }
  });
};

function onSubmit() {
  emailCheckLoading.value = true;
  form.$v.value.$touch();
  if (!form.$v.value.$invalid) {
    authStore
      .userCheckEmail(
        form.values.email,
        form.values.phone_number?.replace(/\s/g, ""),
      )
      .then(() => {
        step.value = 2;
      })
      .catch((err) => {
        if (err?.response?.data?.email) {
          showToast(
            err?.response?.data?.email?.[0] || t("check_login_password"),
            "error",
          );
        }
        if (err?.response?.data?.phone_number) {
          showToast(
            err?.response?.data?.phone_number?.[0] || t("check_login_password"),
            "error",
          );
        }
      })
      .finally(() => {
        emailCheckLoading.value = false;
      });
  }
}

function getCountries(search?: string) {
  loading.value = true;
  ApiService.query("/user/common/country/", {
    params: {
      page: currentPage.value,
      search,
    },
  })
    .then((res) => {
      if (search) {
        regions.value = res.data.results;
      } else {
        regions.value = [...regions.value, ...res.data.results];
      }
      countriesCount.value = res.data?.total_pages;
    })
    .catch(({ response }) => {
      handleError(response);
    })
    .finally(() => (loading.value = false));
}

function closeSelect() {
  isFocused.value = true;
  search.value = "";
}
function onLoadCountries() {
  if (currentPage.value < countriesCount.value) {
    currentPage.value++;
    getCountries();
  }
}
watch(
  () => search.value,
  (val) => {
    debounce("search", () => getCountries(val));
  },
);
watch(
  () => route.query.referral_number,
  (val) => {
    if (val) {
      form.values.showReferral = true;
      form.values.referral_number = String(route.query.referral_number);
    }
  },
  { deep: true, immediate: true },
);
const transitionName = ref("slide-right");
watch(
  () => step.value,
  (newValue, oldValue) => {
    if (newValue < oldValue) {
      transitionName.value = "slide-left";
    } else {
      transitionName.value = "slide-right";
    }
  },
);
// const iso2 = ref("");
// const country = ref("");
// function getCountryName(lat: number, lng: number) {
//   const apiKey = "9191d860-42c5-4e70-98aa-8c92721bbdc3"; // Replace with your Google Maps Geocoding API key
//   const url = `https://geocode-maps.yandex.ru/1.x/?apikey=${apiKey}&geocode=${lng},${lat}&format=json`;
//
//   fetch(url)
//     .then((response) => response.json())
//     .then((data) => {
//       iso2.value =
//         data?.response?.GeoObjectCollection?.featureMember?.[0]?.GeoObject?.metaDataProperty?.GeocoderMetaData?.Address?.country_code;
//       country.value =
//         data?.response?.GeoObjectCollection?.featureMember?.[0]?.GeoObject?.metaDataProperty?.GeocoderMetaData?.Address?.Components?.find(
//           (el) => el?.kind === "country",
//         )?.name;
//     })
//     .catch((error) => {
//       console.error("Fetch error:", error);
//     });
// }

const single = ref("");
function getSingle() {
  ApiService.get(`common/static/terms-conditions/`)
    .then((res) => {
      single.value = res.data;
    })
    .catch((e) => console.log(e.message));
}

onMounted(() => {
  getSingle();
  // navigator.geolocation.getCurrentPosition((position) => {
  //   getCountryName(position?.coords?.latitude, position?.coords?.longitude);
  // });
  if (!form.values.country) {
    getCountries();
  }
  pushToDataLayer("pageview", {
    page: "register",
    event_category: "page",
    event_label: "register_page_view",
    button_text: "None",
  });
});
</script>
